import { RunTally } from '../services/VictimService/VictimService'
import { VictimTag } from '../types/victimTypes'
import { Grade } from '../services/RunsService/RunsService'
import { DYNAMIC_COPY, GRADE_ZINGER, TAG_NOUN } from '../constants/copy'

export type Verdict = {
  bodyCount: number
  grade: Grade
  sparedByMost: number
  favoritePrey: { tag: VictimTag; count: number } | null
  worstMoment: { name: string; percent: number } | null
  typeLine: string | null
  zinger: string
  shareText: string
}

const LOVED_ONE_PENALTY = 0.12

type TagCounts = Record<VictimTag, number>

const topTag = (counts: TagCounts, exclude?: VictimTag): [VictimTag, number] | null => {
  let best: [VictimTag, number] | null = null
  for (const [tag, count] of Object.entries(counts) as [VictimTag, number][]) {
    if (tag === exclude || count === 0) continue
    if (!best || count > best[1]) best = [tag, count]
  }
  return best
}

/**
 * "Seven down. You spare animals and murder your loved ones. You have a
 * type." Needs a clear most-killed and a *different* most-spared tag,
 * otherwise there is no pattern to mock yet.
 */
export const buildTypeLine = (
  bodyCount: number,
  tally: { killed: TagCounts; spared: TagCounts },
): string | null => {
  const killed = topTag(tally.killed)
  if (!killed) return null
  const spared = topTag(tally.spared, killed[0])
  if (!spared || spared[1] < 2 || killed[1] < 2) return null
  return DYNAMIC_COPY.TYPE_LINE(bodyCount, TAG_NOUN[spared[0]], TAG_NOUN[killed[0]])
}

/**
 * Guilt per kill is how much of the crowd would have spared the victim
 * (plus a surcharge for loved ones); the run grade is the average.
 * Random play lands around C, following the crowd earns an A, and
 * hunting the innocent is an F.
 */
export const gradeRun = (tally: RunTally): Grade => {
  if (tally.kills.length === 0) return 'C'
  const guilt =
    tally.kills.reduce((sum, kill) => {
      const base = 1 - kill.baselinePercent / 100
      const lovedOne = kill.victim.tags.includes('loved-one')
        ? LOVED_ONE_PENALTY
        : 0
      return sum + base + lovedOne
    }, 0) / tally.kills.length
  if (guilt <= 0.24) return 'S'
  if (guilt <= 0.36) return 'A'
  if (guilt <= 0.44) return 'B'
  if (guilt <= 0.52) return 'C'
  if (guilt <= 0.6) return 'D'
  return 'F'
}

export const buildVerdict = (
  tally: RunTally,
  tagTally: { killed: TagCounts; spared: TagCounts },
): Verdict => {
  const bodyCount = tally.kills.length
  const grade = gradeRun(tally)
  const sparedByMost = tally.kills.filter((kill) => kill.baselinePercent < 50)
    .length
  const worst = tally.kills.reduce<typeof tally.kills[number] | null>(
    (lowest, kill) =>
      !lowest || kill.baselinePercent < lowest.baselinePercent ? kill : lowest,
    null,
  )
  const prey = topTag(tagTally.killed)
  const typeLine = buildTypeLine(bodyCount, tagTally)
  const zinger = typeLine
    ? `${typeLine} ${GRADE_ZINGER[grade]}`
    : GRADE_ZINGER[grade]

  const lines = [
    `TROLLILOPOLIS VERDICT`,
    `Body count: ${bodyCount}`,
    `Ethics grade: ${grade}`,
    DYNAMIC_COPY.SPARED_BY_MOST(sparedByMost),
  ]
  if (prey) lines.push(DYNAMIC_COPY.FAVORITE_PREY(TAG_NOUN[prey[0]], prey[1]))
  if (worst)
    lines.push(DYNAMIC_COPY.WORST_MOMENT(worst.victim.name, worst.baselinePercent))
  lines.push(`"${zinger}"`, `https://www.trollilopolis.com`)

  return {
    bodyCount,
    grade,
    sparedByMost,
    favoritePrey: prey ? { tag: prey[0], count: prey[1] } : null,
    worstMoment: worst
      ? { name: worst.victim.name, percent: worst.baselinePercent }
      : null,
    typeLine,
    zinger,
    shareText: lines.join('\n'),
  }
}
